import { z } from 'zod';
import { kePhoneSchema } from './common.schemas';
import { newPasswordSchema } from './auth.schemas';
import type { SignInCodeChannel } from './login-code.schemas';

/**
 * The `reason` on a 403 from a change that needs the person to prove it's them first (password, two-factor, email,
 * mobile): the web app asks for a code (`POST /account/confirmation/send`, then `POST /account/confirmation`) and retries.
 */
export const CONFIRMATION_REQUIRED_REASON = 'confirmation_required';

/** Response of `POST /account/confirmation/send`: where the code went, and the code itself on a deployment that shows codes. */
export interface AccountConfirmationSent {
  channel: SignInCodeChannel;
  displayedCode: string | null;
}

const codeSchema = z.string().regex(/^\d{6}$/, 'Enter the 6-digit code');

/** `POST /account/confirmation`, and finishing an email or mobile change: the code that was sent. */
export const accountCodeSchema = z.object({ code: codeSchema }).strict();
export type AccountCodeInput = z.infer<typeof accountCodeSchema>;

/** `PATCH /account/profile`. */
export const updateProfileSchema = z.object({ name: z.string().trim().min(1).max(120) }).strict();
export type UpdateProfileInput = z.infer<typeof updateProfileSchema>;

/** `PUT /account/password`: sets a first password or replaces the one there is. */
export const setPasswordSchema = z.object({ password: newPasswordSchema }).strict();
export type SetPasswordInput = z.infer<typeof setPasswordSchema>;

/** `POST /account/email/send-code`: a code goes to the new address, which is saved once the code comes back. */
export const startEmailChangeSchema = z.object({ email: z.email().trim().toLowerCase() }).strict();
export type StartEmailChangeInput = z.infer<typeof startEmailChangeSchema>;

/** `POST /account/phone/send-code`: a code goes to the new mobile, which is saved once the code comes back. */
export const startPhoneChangeSchema = z.object({ phone: kePhoneSchema }).strict();
export type StartPhoneChangeInput = z.input<typeof startPhoneChangeSchema>;
export type StartPhoneChange = z.output<typeof startPhoneChangeSchema>;
